import { useState } from 'react'
import type { AppConfig, GalleryImage } from '../types/config'
import { ColorSelector } from './ColorSelector'
import { StyleSelector } from './StyleSelector'
import { PreviewModal } from './PreviewModal'
import { generateIcon } from '../api/client'

interface GeneratorProps {
  config: AppConfig
  onNewImage: (image: GalleryImage) => void
}

type Status = 'idle' | 'generating' | 'success' | 'error'

export function Generator({ config, onNewImage }: GeneratorProps) {
  const [text, setText] = useState('')
  const [selectedColor, setSelectedColor] = useState(config.colors[0]?.id || '')
  const [selectedStyle, setSelectedStyle] = useState(config.styles[0]?.id || '')
  const [status, setStatus] = useState<Status>('idle')
  const [message, setMessage] = useState('')
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null)
  const [showPreview, setShowPreview] = useState(false)

  const { ui } = config

  const handleGenerate = async () => {
    if (!text.trim()) return

    setStatus('generating')
    setMessage(ui.generatingText || 'Generating...')
    setGeneratedImageUrl(null)

    try {
      const response = await generateIcon({
        text: text.trim(),
        colorId: selectedColor,
        styleId: selectedStyle,
      })

      if (response.success && response.imageUrl) {
        setStatus('success')
        setMessage(ui.successMessage || 'Icon generated successfully!')
        setGeneratedImageUrl(response.imageUrl)
        
        // Add to gallery
        onNewImage({
          name: response.imageName || `icon_${Date.now()}`,
          url: response.imageUrl,
          createdAt: new Date().toISOString(),
          style: selectedStyle,
          color: selectedColor,
        })
      } else {
        throw new Error(response.message || 'Generation failed')
      }
    } catch (error) {
      setStatus('error')
      setMessage(error instanceof Error ? error.message : (ui.errorMessage || 'Generation failed'))
    }
  }

  const isGenerating = status === 'generating'

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 space-y-8">
        {/* Text Input */}
        <div className="space-y-2">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={ui.textInputPlaceholder || 'Enter icon concept...'}
            className="w-full px-5 py-4 text-lg border-2 border-gray-200 rounded-xl focus:border-exness-yellow focus:outline-none transition-colors placeholder:text-gray-400"
            disabled={isGenerating}
          />
        </div>

        {/* Color Selector */}
        <ColorSelector
          colors={config.colors}
          selectedId={selectedColor}
          onSelect={setSelectedColor}
          label={ui.colorSelectorLabel}
        />

        {/* Style Selector */}
        <StyleSelector
          styles={config.styles}
          selectedId={selectedStyle}
          onSelect={setSelectedStyle}
          label={ui.styleSelectorLabel}
        />

        {/* Generate Button */}
        <div className="space-y-4">
          <button
            onClick={handleGenerate}
            disabled={isGenerating || !text.trim()}
            className={`
              w-full py-4 px-6 text-lg font-semibold rounded-xl transition-all duration-200
              ${isGenerating || !text.trim()
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-exness-yellow hover:bg-yellow-400 text-exness-dark hover:shadow-lg active:scale-[0.98]'
              }
            `}
          >
            {isGenerating ? (
              <span className="flex items-center justify-center gap-3">
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {ui.generatingText || 'Generating...'}
              </span>
            ) : (
              ui.generateButtonText || 'Generate Icon'
            )}
          </button>

          {/* Status Message */}
          {status !== 'idle' && (
            <div 
              className={`
                text-center py-3 px-4 rounded-xl animate-fade-in
                ${status === 'success' ? 'bg-green-50 text-green-700' : ''}
                ${status === 'error' ? 'bg-red-50 text-red-700' : ''}
                ${status === 'generating' ? 'bg-yellow-50 text-yellow-700' : ''}
              `}
            >
              <p className="font-medium">{message}</p>
              
              {/* Preview Button */}
              {status === 'success' && generatedImageUrl && (
                <button
                  onClick={() => setShowPreview(true)}
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-exness-dark text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {ui.previewButtonText || 'Preview'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {generatedImageUrl && (
        <PreviewModal
          imageUrl={generatedImageUrl}
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
          downloadText={ui.downloadButtonText}
          closeText={ui.closeButtonText}
        />
      )}
    </div>
  )
}
